import React, { useEffect, useCallback } from 'react';
import { postService, likeService } from '../services/supabaseService';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';
import type { Profile, Post as DBPost } from '../types/database';

export interface Post {
  id: string;
  author: string;
  username: string;
  avatar?: string;
  authorId: string;
  timestamp: string;
  type: 'listing' | 'story' | 'post' | 'fit_pic' | 'moodboard' | 'poll' | 'challenge';
  content?: string;
  images?: string[];
  // Listing specific
  brand?: string;
  model?: string;
  size?: string;
  price?: number;
  year?: string;
  colors?: string[];
  reviews?: { rating: number; count: number };
  // Interaction
  likes: number;
  comments: number;
  liked?: boolean;
  tags?: string[];
}

export interface FeedContextType {
  posts: Post[];
  filteredPosts: Post[];
  filter: 'listings' | 'stories' | 'all';
  loading: boolean;
  setFilter: (filter: 'listings' | 'stories' | 'all') => void;
  getPosts: () => Promise<void>;
  refreshPosts: () => Promise<void>;
  createPost: (content: string, type: string, images?: File[], tags?: string[]) => Promise<{ error: string | null }>;
  likePost: (postId: string) => Promise<void>;
  deletePost: (postId: string) => Promise<void>;
}

function formatTimestamp(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

function mapDbPostToFeedPost(dbPost: DBPost & { author: Profile }, liked = false): Post {
  return {
    id: dbPost.id,
    author: dbPost.author?.display_name || 'Unknown',
    username: dbPost.author?.username || 'unknown',
    avatar: dbPost.author?.avatar_url || undefined,
    authorId: dbPost.author_id,
    timestamp: formatTimestamp(dbPost.created_at),
    type: dbPost.type as Post['type'],
    content: dbPost.content,
    images: (dbPost.images as string[]) || [],
    tags: (dbPost.tags as string[]) || [],
    likes: dbPost.likes_count,
    comments: dbPost.comments_count,
    liked,
  };
}

export const FeedContext = React.createContext<FeedContextType | undefined>(undefined);

export const FeedProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [posts, setPosts] = React.useState<Post[]>([]);
  const [filter, setFilter] = React.useState<'listings' | 'stories' | 'all'>('all');
  const [loading, setLoading] = React.useState(false);
  const hasFetched = React.useRef(false);
  const realtimeActive = React.useRef(false);
  const fetchingRef = React.useRef(false);

  const getPosts = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    hasFetched.current = true;
    try {
      const typeFilter = filter === 'listings' ? 'listing' : filter === 'stories' ? 'story' : undefined;
      const { data, error } = await postService.getFeed({ limit: 50, type: typeFilter });

      if (error) {
        console.error('Error fetching posts:', error);
        setLoading(false);
        fetchingRef.current = false;
        return;
      }

      let likedIds = new Set<string>();
      if (user && data.length > 0) {
        likedIds = await likeService.getLikedPostIds(
          user.id,
          data.map(p => p.id)
        );
      }

      const feedPosts = data.map(p => mapDbPostToFeedPost(p, likedIds.has(p.id)));
      setPosts(feedPosts);
    } catch (err) {
      console.error('Feed fetch error:', err);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [filter, user]);

  // Re-fetch when filter or user changes (user needed for liked status)
  useEffect(() => {
    if (hasFetched.current) {
      getPosts();
    }
  }, [filter, getPosts]);

  // Only start realtime subscription after the feed has been fetched once
  useEffect(() => {
    if (!hasFetched.current || realtimeActive.current) return;
    realtimeActive.current = true;

    const channel = supabase
      .channel('feed-realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'posts' },
        async (payload) => {
          const { data } = await postService.getById(payload.new.id);
          if (data) {
            const newPost = mapDbPostToFeedPost(data);
            setPosts(prev => [newPost, ...prev]);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      realtimeActive.current = false;
    };
  }, [posts.length > 0]);

  const createPost = async (content: string, type: string, imageFiles?: File[], tags?: string[]): Promise<{ error: string | null }> => {
    if (!user) return { error: 'Must be logged in' };

    let imageUrls: string[] = [];
    if (imageFiles && imageFiles.length > 0) {
      imageUrls = await postService.uploadImages(imageFiles);
    }

    const { error } = await postService.create({
      author_id: user.id,
      content,
      type,
      images: imageUrls,
      tags: tags || [],
    });

    if (error) return { error: error.message };

    // Feed will update via real-time subscription
    return { error: null };
  };

  const likePost = async (postId: string) => {
    if (!user) return;

    // Optimistic update
    setPosts(prev =>
      prev.map(p =>
        p.id === postId
          ? { ...p, liked: !p.liked, likes: p.liked ? p.likes - 1 : p.likes + 1 }
          : p
      )
    );

    const { liked } = await likeService.toggle(postId, user.id);

    // If server disagrees with optimistic update, correct it
    setPosts(prev =>
      prev.map(p => {
        if (p.id !== postId) return p;
        return { ...p, liked };
      })
    );
  };

  const deletePost = async (postId: string) => {
    setPosts(prev => prev.filter(p => p.id !== postId));
    await postService.delete(postId);
  };

  const filteredPosts = filter === 'all'
    ? posts
    : posts.filter(p =>
      filter === 'listings' ? p.type === 'listing' : p.type === 'story'
    );

  return (
    <FeedContext.Provider value={{ posts, filteredPosts, filter, loading, setFilter, getPosts, refreshPosts: getPosts, createPost, likePost, deletePost }}>
      {children}
    </FeedContext.Provider>
  );
};

export const useFeed = () => {
  const context = React.useContext(FeedContext);
  if (!context) {
    throw new Error('useFeed must be used within FeedProvider');
  }
  return context;
};
